import { Server } from '@modelcontextprotocol/sdk/server/index.js';
import { StdioServerTransport } from '@modelcontextprotocol/sdk/server/stdio.js';
import type { Request } from '@modelcontextprotocol/sdk/types.js';
import type { IFIXParser, Message } from 'fixparser';
import { z } from 'zod';

import { MCPBase } from './MCPBase';
import type { PluginOptions } from './PluginOptions';
import type { VerifiedOrder } from './schemas';
import type { MarketDataEntry } from './schemas/marketData';
import { toolSchemas } from './schemas/schemas';
import { createToolHandlers } from './tools';
import { handleMessage } from './utils/messageHandler';

export class MCPLocal extends MCPBase {
    /**
     * Map to store verified orders before execution
     * @private
     */
    protected override verifiedOrders: Map<string, VerifiedOrder> = new Map();

    /**
     * Map to store pending requests and their callbacks
     * @private
     */
    protected override pendingRequests: Map<string, (data: Message) => void> = new Map();

    /**
     * Map to store market data prices for each symbol
     * @private
     */
    protected override marketDataPrices: Map<string, MarketDataEntry[]> = new Map();

    /**
     * Maximum number of price history entries to keep per symbol
     * @private
     */
    protected override readonly MAX_PRICE_HISTORY = 100000;

    private server: Server = new Server(
        {
            name: 'fixparser',
            version: '1.0.0',
        },
        {
            capabilities: {
                tools: Object.entries(toolSchemas).reduce(
                    (acc, [name, { description, schema }]) => {
                        acc[name] = {
                            description,
                            parameters: schema,
                        };
                        return acc;
                    },
                    {} as Record<string, { description: string; parameters: any }>,
                ),
            },
        },
    );
    private transport: StdioServerTransport = new StdioServerTransport();

    constructor({ logger, onReady }: PluginOptions) {
        super({ logger, onReady });
    }

    public async register(parser: IFIXParser): Promise<void> {
        this.parser = parser;
        this.parser.addOnMessageCallback((message: Message) => {
            handleMessage(message, this.parser!, this.pendingRequests, this.marketDataPrices, this.MAX_PRICE_HISTORY);
        });

        this.addWorkflows();

        await this.server.connect(this.transport);

        if (this.onReady) {
            this.onReady();
        }
    }

    private addWorkflows() {
        if (!this.parser) {
            return;
        }

        if (!this.server) {
            return;
        }

        // Add handler for tools/list
        this.server.setRequestHandler(z.object({ method: z.literal('tools/list') }), async () => {
            return {
                tools: Object.entries(toolSchemas).map(([name, { description, schema }]) => ({
                    name,
                    description,
                    inputSchema: schema,
                })),
            };
        });

        // Add handler for tools/call
        this.server.setRequestHandler(
            z.object({
                method: z.literal('tools/call'),
                params: z.object({
                    name: z.string(),
                    arguments: z.any(),
                    _meta: z
                        .object({
                            progressToken: z.number(),
                        })
                        .optional(),
                }),
            }),
            async (request: Request) => {
                const { name, arguments: args } = request.params as { name: string; arguments: any };

                const toolHandlers = createToolHandlers(
                    this.parser!,
                    this.verifiedOrders,
                    this.pendingRequests,
                    this.marketDataPrices,
                );

                const handler = toolHandlers[name];
                if (!handler) {
                    return {
                        content: [
                            {
                                type: 'text',
                                text: `Tool not found: ${name}`,
                                uri: name,
                            },
                        ],
                        isError: true,
                    };
                }

                const result = await handler(args);
                return {
                    content: result.content,
                    isError: result.isError,
                };
            },
        );

        process.on('SIGINT', async () => {
            await this.server.close();
            process.exit(0);
        });
    }
}
